import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useConfirmEecsTransactionPaymentMutation,
  useLazyFetchEecsTransactionsQuery,
} from '../api/eecs-transaction.api';
import {
  EecsTransactionAC,
  setEecsTransactionModal,
  setSelectedEecsTransaction,
} from '../store/eecs-transaction.slice';
import {
  ConfirmEecsTransactionPaymentPayload,
  FetchEecsTransactionPayload,
  IEecsTransaction,
  IEecsTransactionModal,
} from '../types/eecs-transaction.type';

interface OpenModal {
  modal: IEecsTransactionModal;
  transaction: IEecsTransaction;
}
interface UseEecsTransaction {
  fetchEecsTransactionsHandler: (payload: FetchEecsTransactionPayload) => Promise<boolean>;

  confirmEecsTransactionPaymentHandler: (payload: ConfirmEecsTransactionPaymentPayload) => Promise<boolean>;
  openEecsTransactionsModal: ({ modal, transaction }: OpenModal) => void;
  clearEecsTransactionsState: () => void;
  closeEecsTransactionsModal: () => void;
}

export const useEecsTransaction = (): UseEecsTransaction => {
  const dispatch = useAppDispatch();
  const [fetchEecsTransactionsQuery] = useLazyFetchEecsTransactionsQuery();
  const [confirmEecsTransactionPaymentQuery] = useConfirmEecsTransactionPaymentMutation();

  const confirmEecsTransactionPaymentHandler = async (payload: ConfirmEecsTransactionPaymentPayload) => {
    const { error } = await confirmEecsTransactionPaymentQuery(payload);
    const isError = !!error;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to confirm EECS Transaction payment!');
    }
    return !isError;
  };

  const fetchEecsTransactionsHandler = async (payload: FetchEecsTransactionPayload) => {
    dispatch(EecsTransactionAC.toggleLoading(true));
    const { data: response, error } = await fetchEecsTransactionsQuery(payload);
    dispatch(EecsTransactionAC.toggleLoading(false));

    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Transactions!');
    } else {
      batch(() => {
        dispatch(EecsTransactionAC.setItems(response.data));
        dispatch(EecsTransactionAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const clearEecsTransactionsState = () => {
    dispatch(EecsTransactionAC.clearItems());
    dispatch(EecsTransactionAC.clearFilters());
    dispatch(EecsTransactionAC.setCurrentPage(1));
    dispatch(EecsTransactionAC.clearSorts());
    dispatch(setSelectedEecsTransaction(null));
    dispatch(setEecsTransactionModal(null));
  };

  const openEecsTransactionsModal = ({ modal, transaction }: OpenModal) => {
    dispatch(setSelectedEecsTransaction(transaction));
    dispatch(setEecsTransactionModal(modal));
  };

  const closeEecsTransactionsModal = () => {
    dispatch(setSelectedEecsTransaction(null));
    dispatch(setEecsTransactionModal(null));
  };

  return {
    fetchEecsTransactionsHandler,
    clearEecsTransactionsState,
    closeEecsTransactionsModal,
    openEecsTransactionsModal,
    confirmEecsTransactionPaymentHandler,
  };
};
